import * as dateFns from 'date-fns'
import { addMonths, parseISO } from 'date-fns'
import React from 'react'

import { getDate } from '../../../app/header/utils'
import { KeyStat } from '../../components/keystats/KeyStat'
import { KeyStats } from '../../components/keystats/KeyStats'
import { getEarningsDateSuffix, shortFormatDollar } from '../../util'
import { EARNINGS_DATE_FORMAT } from './constants'
import { EarningsData } from './types'
import { getFormattedPeriodValue, getIsEarningEstimate, getIsSemiAnnualEarning } from './utils'

function getMonths(isAnnualEarning: boolean, isSemiAnnualEarning: boolean) {
  if (isAnnualEarning) return 12
  if (isSemiAnnualEarning) return 6
  return 3
}

type WidgetHeaderProps = Pick<EarningsData, 'earningsDate' | 'earningsData' | 'earningsAnnualData'>

export default function EarningsHeader({ earningsDate, earningsData, earningsAnnualData }: WidgetHeaderProps) {
  const isAnnualEarning = !earningsData.length
  const isSemiAnnualEarning = getIsSemiAnnualEarning(earningsData)
  const nextReportInfo = React.useMemo(() => {
    const data = isAnnualEarning ? earningsAnnualData : earningsData
    const parsedEarningsDate = earningsDate ? parseISO(earningsDate) : null
    const firstEstimateEarningIndex = data.findIndex(getIsEarningEstimate)
    const firstEstimateEarning = data[firstEstimateEarningIndex] ?? null
    const lastEarning = data[firstEstimateEarningIndex - 1] ?? null

    if (lastEarning === null || firstEstimateEarning === null) {
      return {
        date: null,
        nextPeriod: null,
        dateSuffix: '',
        eps: null,
        gaapEps: null,
        revenue: null,
      }
    }

    const lastEarningDate = lastEarning.earningsDate
    const shouldShowEstimatedNextEarningsDate = !(
      parsedEarningsDate && getDate(parsedEarningsDate).getTime() >= Date.now()
    )
    const estimatedEarningsDate = lastEarningDate
      ? addMonths(parseISO(lastEarningDate), getMonths(isAnnualEarning, isSemiAnnualEarning))
      : null

    return {
      date: shouldShowEstimatedNextEarningsDate ? estimatedEarningsDate : parsedEarningsDate,
      nextPeriod: getFormattedPeriodValue(firstEstimateEarning.fiscalPeriod, isAnnualEarning, isSemiAnnualEarning),
      dateSuffix:
        shouldShowEstimatedNextEarningsDate && estimatedEarningsDate
          ? '(Est)'
          : getEarningsDateSuffix(parsedEarningsDate),
      eps: firstEstimateEarning?.epsEstimate,
      gaapEps: firstEstimateEarning.epsReportedEstimate,
      revenue: firstEstimateEarning?.salesEstimate,
    }
  }, [earningsDate, earningsData, earningsAnnualData, isAnnualEarning, isSemiAnnualEarning])

  return (
    <KeyStats className="my-6">
      <KeyStat
        datapoint={`${nextReportInfo.date ? dateFns.format(nextReportInfo.date, EARNINGS_DATE_FORMAT) : '—'} ${nextReportInfo.dateSuffix}`}
        label="Next report date"
      />
      <KeyStat datapoint={nextReportInfo.nextPeriod ?? '—'} label="Report period" />
      <KeyStat datapoint={shortFormatDollar(nextReportInfo.eps, { defaultValue: '—' })} label="EPS Est." />
      <KeyStat datapoint={shortFormatDollar(nextReportInfo.gaapEps, { defaultValue: '—' })} label="GAAP EPS Est." />
      <KeyStat
        datapoint={shortFormatDollar(nextReportInfo.revenue, { defaultValue: '—', multiply: 1_000_000 })}
        label="Revenue Est."
      />
    </KeyStats>
  )
}
